import React, { useMemo, useState } from "react";

import { faServer } from "@fortawesome/pro-regular-svg-icons";
import { IFuseOptions } from "fuse.js";

import { EditLlmQuotaModal } from "src/adminPanel/modelQuotas/EditLlmQuotaModal";
import {
  buildModelQuotaRows,
  ModelQuotaRow,
  ModelQuotasTable,
} from "src/adminPanel/modelQuotas/ModelQuotasTable";
import { useAllAWSAccounts, useAllOrgs } from "src/adminPanel/queries";
import { SearchInput, useUrlSyncedSearch } from "src/adminPanel/SearchInput";
import { Module, useModulesByKind } from "src/api/modulesApi";
import { useWorkspaces } from "src/api/queries";
import { Checkbox } from "src/base-components/Checkbox";
import { ModuleKind } from "src/clients/flow-api";
import { EmptyState } from "src/design-system/EmptyState";
import { useModal } from "src/design-system/Modal";
import { Skeleton } from "src/design-system/Skeleton";
import { useFuseSearch } from "src/utils/useFuseSearch";

const SEARCH_OPTIONS: IFuseOptions<ModelQuotaRow> = {
  keys: ["workspaceId", "workspaceName", "workspaceSlug", "orgId", "orgName"],
  threshold: 0.3,
  ignoreLocation: true,
};

export const ModelQuotasSettings: React.FC = () => {
  const {
    data: workspaces,
    isLoading: workspacesLoading,
    isError: workspacesError,
  } = useWorkspaces(true, true);
  const {
    data: organizations,
    isLoading: orgsLoading,
    isError: orgsError,
  } = useAllOrgs();
  const {
    allModules,
    isLoading: modulesLoading,
    isError: modulesError,
  } = useModulesByKind();
  const {
    data: awsAccounts,
    isLoading: awsAccountsLoading,
    isError: awsAccountsError,
  } = useAllAWSAccounts(true);

  const { initialSearch, debouncedSearchTerm, handleDebouncedSearchChange } =
    useUrlSyncedSearch();

  const {
    isOpen: isEditOpen,
    openModal: openEditModal,
    closeModal: closeEditModal,
    afterLeave: afterEditLeave,
    data: editRow,
  } = useModal<ModelQuotaRow>();

  const orgMap = useMemo(
    () => new Map((organizations ?? []).map((org) => [org.id, org.name])),
    [organizations],
  );

  const agentsModuleByWorkspaceId = useMemo(
    () =>
      new Map<string, Module>(
        allModules
          .filter((m) => m.kind === ModuleKind.AGENTS)
          .map((m) => [m.workspaceId, m] as const),
      ),
    [allModules],
  );

  const awsAccountNameByUuid = useMemo(
    () => new Map((awsAccounts ?? []).map((a) => [a.id, a.name])),
    [awsAccounts],
  );

  const rows = useMemo(
    () =>
      buildModelQuotaRows(
        workspaces ?? [],
        orgMap,
        agentsModuleByWorkspaceId,
        awsAccountNameByUuid,
      ),
    [workspaces, orgMap, agentsModuleByWorkspaceId, awsAccountNameByUuid],
  );

  const [showDriftOnly, setShowDriftOnly] = useState(false);

  const fuseSearch = useFuseSearch(rows, SEARCH_OPTIONS);
  const filteredRows = useMemo(() => {
    const searched = fuseSearch(debouncedSearchTerm);
    return showDriftOnly
      ? searched.filter((row) => row.drift.hasDrift)
      : searched;
  }, [fuseSearch, debouncedSearchTerm, showDriftOnly]);
  const driftCount = useMemo(
    () => rows.filter((row) => row.drift.hasDrift).length,
    [rows],
  );

  const isLoading =
    workspacesLoading || orgsLoading || modulesLoading || awsAccountsLoading;
  const isError =
    workspacesError || orgsError || modulesError || awsAccountsError;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="mx-auto max-w-screen-2xl">
          <div className="mb-8 space-y-2">
            <Skeleton height="h-8" width="w-70" />
            <Skeleton height="h-5" width="w-96" />
          </div>
          <Skeleton height="h-10" width="w-full" />
          <div className="mt-6 space-y-3">
            <Skeleton height="h-12" width="w-full" />
            <Skeleton height="h-12" width="w-full" />
            <Skeleton height="h-12" width="w-full" />
            <Skeleton height="h-12" width="w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="mx-auto max-w-screen-2xl">
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <EmptyState
              description="Failed to load workspaces. Please try again."
              headline="Error loading model quotas"
              icon={faServer}
            />
          </div>
        </div>
      </div>
    );
  }

  const editOrgName = editRow ? orgMap.get(editRow.orgId) : undefined;

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="mx-auto max-w-screen-2xl">
        <div className="mb-8">
          <h1 className="font-inter-semibold-32px text-gray-900">
            Model quotas
          </h1>
          <p className="mt-2 font-inter-normal-16px text-gray-600">
            Configure LLM usage tier, cost limit, features, and per-model
            overrides per workspace.
          </p>
        </div>

        <div className="mb-6 flex items-center gap-4">
          <div className="flex-1">
            <SearchInput
              dataLoc="model-quotas-search"
              initialValue={initialSearch}
              placeholder="Search by organization, workspace, or workspace ID..."
              onDebouncedChange={handleDebouncedSearchChange}
            />
          </div>
          <label className="flex items-center gap-2 font-inter-normal-13px text-gray-700">
            <Checkbox
              checked={showDriftOnly}
              dataLoc="model-quotas-drift-filter"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setShowDriftOnly(e.target.checked)
              }
            />
            <span>Show only config mismatches ({driftCount})</span>
          </label>
        </div>

        <div className="mb-3 font-inter-normal-13px text-gray-500">
          {filteredRows.length} workspace
          {filteredRows.length !== 1 ? "s" : ""}
          {debouncedSearchTerm && ` matching "${debouncedSearchTerm}"`}
          {showDriftOnly && " with config mismatches"}
        </div>

        {filteredRows.length === 0 ? (
          <div className="rounded-xl border border-gray-200 bg-white py-12 text-center font-inter-normal-13px text-gray-500 shadow-sm">
            {showDriftOnly
              ? "No workspaces with config mismatches"
              : debouncedSearchTerm
                ? `No workspaces matching "${debouncedSearchTerm}"`
                : "No workspaces"}
          </div>
        ) : (
          <ModelQuotasTable rows={filteredRows} onEdit={openEditModal} />
        )}

        <EditLlmQuotaModal
          afterLeave={afterEditLeave}
          agentsModuleId={editRow?.agentsModuleId}
          awsAccountName={editRow?.awsAccountName}
          isOpen={isEditOpen}
          orgName={editOrgName}
          workspace={editRow?.workspace}
          onClose={closeEditModal}
        />
      </div>
    </div>
  );
};
